// Mixin that deals with push notification, including web push, FCM, and APNS
// Check the flowcart below for details
// Also, more details - https://github.com/havesource/cordova-plugin-push

import { i18n } from '@/i18n';

let currentPushSubscription = null;
let lastTsRequestedPerm = 0;

export default {
  methods: {
    // following methods all relates to notifcation setup flow
    // #askPushNotification -> if not needed -> return
    //  |               |   -> if not supported -> return
    //  | if has perm   |
    //  |               | if no perm
    //  |               v
    //  |   #showAskPushNotificationToast -> if user said stop -> return
    //  |               |                 -> if user denied -> return
    //  |               |                           | if user denied 3 times
    //  |               |                           v
    //  |               |                      showStop?Toast -> return
    //  v               v
    // #requestPushNotification (invoke the native perm dialog) -> if denied -> return
    //         |
    //         v
    // #setupPushNotification -> if no perm and didn't skip perm check -> return
    //         |              -> if no perm and skipped perm check -> #showPushNotificationDeniedToast
    //         v
    // sync to backend
    askPushNotification({ force = false } = {}) {
      if (this.currentUser.all_notf_disabled || !this.currentUser.new_ep_from_sub_push_notf_enabled) { // request notification permision only when subscribed to something and notification is enabled in user settings
        return;
      }
      if (this.$isCordova) {
        this.askPushNotificationCordova({ force: force });
        return;
      }
      if (!window.Notification || !window.Notification.permission) { return; }
      if (window.Notification.permission === 'denied') { return; }
      if (window.Notification.permission === 'granted') {
        this.requestPushNotification();
        return;
      }
      this.showAskPushNotificationToast({ force: force });
    },
    askPushNotificationCordova({ force = false } = {}) {
      if (!this.$isCordova) { return; }
      let that = this;
      document.addEventListener('deviceready', () => {
        window.PushNotification.hasPermission(data => {
          if (data.isEnabled) {
            that.requestPushNotification();
            return;
          }
          that.showAskPushNotificationToast({ force: force });
        });
      }, false);
    },
    showAskPushNotificationToast({ force = false } = {}) {
      let that = this;
      let stopAskNotificationPermission = !!parseInt(window.localStorage.getItem('stop_ask_notification_permission') || 0);
      let askNotificationPermissionCount = window.localStorage.getItem('ask_notification_permission_count') || 0;
      let currentTs = Math.floor(Date.now() / 1000);
      if ((stopAskNotificationPermission || currentTs < lastTsRequestedPerm + 30) && !force) { return; }
      askNotificationPermissionCount += 1;
      window.localStorage.setItem('ask_notification_permission_count', askNotificationPermissionCount);
      lastTsRequestedPerm = currentTs;
      this.$ons.notification.toast(i18n.t("messages.notf_permission_required"), { buttonLabel: i18n.t("ui.allow"), timeout: 7000, animation: 'fall' }).then((res) => {
          if (res === 0) { // tapped ALLOW
            that.requestPushNotification();
          } else { // closed implicitly
            if (askNotificationPermissionCount >= 3) {
              that.$ons.notification.toast(i18n.t("messages.stop_permission_prompt"), { buttonLabel: i18n.t("ui.yes"), timeout: 7000, animation: 'fall' }).then((res) => {
                  if (res === 0) {
                    window.localStorage.setItem('stop_ask_notification_permission', 1);
                    that.$ons.notification.toast(i18n.t("messages.permission_prompt_stopped"), { timeout: 5000, animation: 'fall' });
                  }
                });
            }
          }
        });
    },
    requestPushNotification() {
      if (this.$isCordova) {
        return this.requestPushNotificationCordova();
      }
      let that = this;
      let askPermission = function() {
        return new Promise(function(resolve, reject) {
          if (window.Notification.permission === 'granted') {
            resolve('granted');
            return;
          }

          const permissionResult = window.Notification.requestPermission(function(result) {
            resolve(result);
          });

          if (permissionResult) {
            permissionResult.then(resolve, reject);
          }
        })
        .then(function(permissionResult) {
          if (permissionResult !== 'granted') {
            throw new Error('We weren\'t granted permission.');
          }
        });
      };
      if (window.Notification) {
        askPermission().then(() => {
          that.setupPushNotification();
        });
      }
    },
    requestPushNotificationCordova() {
      if (!this.$isCordova) { return; }
      this.setupPushNotificationCordova({ skipPermCheck: true });
    },
    setupPushNotification() {
      if (this.currentUser._is_guest) { return; }
      if (this.$isCordova) {
        return this.setupPushNotificationCordova();
      }
      if (!window.Notification || window.Notification.permission !== 'granted') { return; }
      let that = this;
      navigator.serviceWorker.getRegistration().then(function(registration) {
        const subscribeOptions = {
          userVisibleOnly: true,
          applicationServerKey: that.urlBase64ToUint8Array(
            process.env.VUE_APP_VAPID_APPLICATION_SERVER_KEY
          )
        };

        return registration.pushManager.subscribe(subscribeOptions);
      }).then(function(pushSubscription) {
        let updatedAt = parseInt(window.localStorage.getItem('push_subscription_updated_at') || 0);
        let pushSubscriptionStr = window.localStorage.getItem('push_subscription_str');
        let now = Math.floor(Date.now() / 1000);
        let newPushSubscriptionStr = JSON.stringify(pushSubscription);
        if (newPushSubscriptionStr === pushSubscriptionStr && updatedAt + 86400 > now) { return pushSubscription; }
        window.localStorage.setItem('push_subscription_updated_at', now);
        window.localStorage.setItem('push_subscription_str', newPushSubscriptionStr);
        let parsedPushSubscription = JSON.parse(newPushSubscriptionStr);
        that.currentUser.upsertPushSubscription({
          type: 'WebPushNotificationSubscription',
          endpoint: parsedPushSubscription.endpoint,
          key_p256dh: parsedPushSubscription.keys.p256dh,
          key_auth: parsedPushSubscription.keys.auth,
        });
        return pushSubscription;
      });
    },
    setupPushNotificationCordova({ skipPermCheck = false } = {}) {
      if (!this.$isCordova) { return; }
      let that = this;
      document.addEventListener('deviceready', () => {
        window.PushNotification.hasPermission(data => {
          if (!data.isEnabled && !skipPermCheck) { return; }
          let push = window.PushNotification.init({
            android: {
              forceShow: true,
              clearNotifications: false,
            },
            ios: {
              alert: true,
              badge: false,
              sound: true,
            },
          });
          let onRegistrationFn = (pushSubscription) => {
            push.off('registration', onRegistrationFn);
            window.PushNotification.hasPermission(data => {
              if (!data.isEnabled) {
                that.showPushNotificationDeniedToast();
              }
            });
            let updatedAt = parseInt(window.localStorage.getItem('push_subscription_updated_at') || 0);
            let pushSubscriptionStr = window.localStorage.getItem('push_subscription_str');
            let now = Math.floor(Date.now() / 1000);
            let newPushSubscriptionStr = JSON.stringify(pushSubscription);
            if (newPushSubscriptionStr === pushSubscriptionStr && updatedAt + 86400 > now) { return; }
            window.localStorage.setItem('push_subscription_updated_at', now);
            window.localStorage.setItem('push_subscription_str', newPushSubscriptionStr);
            if (pushSubscription.registrationType === 'FCM') {
              that.currentUser.upsertPushSubscription({
                type: 'FcmPushNotificationSubscription',
                token: pushSubscription.registrationId,
              });
              currentPushSubscription = pushSubscription;
            } else if (pushSubscription.registrationType === 'APNS') {
              that.currentUser.upsertPushSubscription({
                type: 'ApnsPushNotificationSubscription',
                token: pushSubscription.registrationId,
              });
              currentPushSubscription = pushSubscription;
            } else {}
          };
          push.on('registration', onRegistrationFn);
          // push.on('notification', (note) => { console.log(note); });
          // let onErrorFn = (error) => {
          //   console.log(error);
          //   push.off('error', onErrorFn);
          // };
          // push.on('error', onErrorFn);
          if ((that.$ons.platform.isIOS() || that.$ons.platform.isIPadOS()) && skipPermCheck) {
            window.setTimeout(() => {
              window.PushNotification.hasPermission(data => {
                if (!data.isEnabled) { that.showPushNotificationDeniedToast(); }
              });
            }, 5000);
          }
          this.setupNotificationClickListenerCordova(push);
        });
      }, false);
    },
    showPushNotificationDeniedToast() {
      this.$ons.notification.toast(i18n.t("messages.notf_permission_denied"), { timeout: 7000, animation: 'fall' });
    },
    destroyPushNotification() {
      if (this.$isCordova) {
        return this.destroyPushNotificationCordova();
      }
      if (!window.Notification || window.Notification.permission !== 'granted') { return Promise.resolve(); }
      let that = this;
      return navigator.serviceWorker.getRegistration().then(function(registration) {
        const subscribeOptions = {
          userVisibleOnly: true,
          applicationServerKey: that.urlBase64ToUint8Array(
            process.env.VUE_APP_VAPID_APPLICATION_SERVER_KEY
          )
        };

        return registration.pushManager.subscribe(subscribeOptions);
      }).then(function(pushSubscription) {
        window.localStorage.setItem('push_subscription_updated_at', 0);
        window.localStorage.setItem('push_subscription_str', '');
        that.currentUser.deletePushSubscription({
          type: 'WebPushNotificationSubscription',
          endpoint: pushSubscription.endpoint,
        });
        return pushSubscription;
      });
    },
    destroyPushNotificationCordova() {
      let that = this;
      window.localStorage.setItem('push_subscription_updated_at', 0);
      window.localStorage.setItem('push_subscription_str', '');
      return new Promise(function(resolve, reject) {
        if (!that.$isCordova) {
          reject('Cordova function called outside cordova env');
          return;
        }
        if (!currentPushSubscription) {
          reject('No push subscription to destroy');
          return;
        }
        let pushSubscription = currentPushSubscription;
        currentPushSubscription = null;

        if (pushSubscription.registrationType === 'FCM') {
          return that.currentUser.deletePushSubscription({
            type: 'FcmPushNotificationSubscription',
            token: pushSubscription.registrationId,
          });
        } else if (pushSubscription.registrationType === 'APNS') {
          return that.currentUser.deletePushSubscription({
            type: 'ApnsPushNotificationSubscription',
            token: pushSubscription.registrationId,
          });
        } else {}
        reject('Unknown push subscirption type');
        return;
      })
    },
  },
};
